import React, { useState } from 'react';
import { PlusCircle, MinusCircle, TrendingUp, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { calculateNegativeGearing, formatCurrency, formatCurrencyDecimal } from '../utils/calculators';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { captureCalculatorLead } from '../data/mock';

export const NegativeGearingCalculator = () => {
  const [taxableIncome, setTaxableIncome] = useState('120000');
  const [properties, setProperties] = useState([
    {
      id: 1,
      purchasePrice: '600000',
      weeklyRent: '550',
      loanAmount: '480000',
      interestRate: '6.0',
      annualRates: '2500',
      annualInsurance: '1500',
      annualMaintenance: '3000',
      annualDepreciation: '8000'
    }
  ]);
  const [results, setResults] = useState(null);

  const addProperty = () => {
    if (properties.length < 5) {
      setProperties([...properties, {
        id: properties.length + 1,
        purchasePrice: '600000',
        weeklyRent: '550',
        loanAmount: '480000',
        interestRate: '6.0',
        annualRates: '2500',
        annualInsurance: '1500',
        annualMaintenance: '3000',
        annualDepreciation: '8000'
      }]);
    }
  };

  const removeProperty = (id) => {
    if (properties.length > 1) {
      setProperties(properties.filter(p => p.id !== id));
    }
  };

  const updateProperty = (id, field, value) => {
    setProperties(properties.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const handleCalculate = async () => {
    const result = calculateNegativeGearing(properties, taxableIncome);
    setResults(result);

    // Capture calculator lead
    if (result) {
      try {
        const inputs = {
          taxableIncome: parseFloat(taxableIncome),
          numberOfProperties: properties.length,
          properties: properties.map(p => ({
            purchasePrice: parseFloat(p.purchasePrice),
            weeklyRent: parseFloat(p.weeklyRent),
            loanAmount: parseFloat(p.loanAmount),
            interestRate: parseFloat(p.interestRate)
          }))
        };

        const resultsData = {
          taxSavings: result.summary.taxSavings,
          weeklyCashFlow: result.summary.weeklyCashFlow,
          annualCashFlow: result.summary.totalCashFlow,
          isNegativelyGeared: result.summary.isNegativelyGeared
        };

        await captureCalculatorLead('negative_gearing', inputs, resultsData);
      } catch (error) {
        console.error('Error capturing calculator lead:', error);
      }
    }
  };

  return (
    <div className="w-full">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card className="border-2 border-[#7A9B76]/20 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Calculator className="w-6 h-6 text-[#D4AF37]" />
                Negative Gearing Calculator
              </CardTitle>
              <CardDescription>Calculate tax benefits and cash flow for up to 5 investment properties</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="space-y-2">
                <Label htmlFor="taxableIncome">Your Annual Taxable Income</Label>
                <Input
                  id="taxableIncome"
                  type="number"
                  value={taxableIncome}
                  onChange={(e) => setTaxableIncome(e.target.value)}
                  placeholder="120000"
                  className="text-lg font-semibold"
                />
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Investment Properties ({properties.length}/5)
                  </h3>
                  <Button
                    onClick={addProperty}
                    disabled={properties.length >= 5}
                    variant="outline"
                    size="sm"
                    className="gap-2 border-[#7A9B76] text-[#7A9B76] hover:bg-[#7A9B76] hover:text-white transition-all"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Add Property
                  </Button>
                </div>

                <Accordion type="single" collapsible className="space-y-3">
                  {properties.map((property, index) => (
                    <AccordionItem 
                      key={property.id} 
                      value={`property-${property.id}`}
                      className="border-2 border-gray-200 rounded-lg px-4 hover:border-[#7A9B76]/50 transition-all"
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="font-semibold text-gray-800">
                            Property {index + 1}
                          </span>
                          {properties.length > 1 && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeProperty(property.id);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <MinusCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">Purchase Price</Label>
                            <Input
                              type="number"
                              value={property.purchasePrice}
                              onChange={(e) => updateProperty(property.id, 'purchasePrice', e.target.value)}
                              placeholder="600000"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Weekly Rent</Label>
                            <Input
                              type="number"
                              value={property.weeklyRent}
                              onChange={(e) => updateProperty(property.id, 'weeklyRent', e.target.value)}
                              placeholder="550"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Loan Amount</Label>
                            <Input
                              type="number"
                              value={property.loanAmount}
                              onChange={(e) => updateProperty(property.id, 'loanAmount', e.target.value)}
                              placeholder="480000"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Interest Rate (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={property.interestRate}
                              onChange={(e) => updateProperty(property.id, 'interestRate', e.target.value)}
                              placeholder="6.0"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Annual Rates</Label>
                            <Input
                              type="number"
                              value={property.annualRates}
                              onChange={(e) => updateProperty(property.id, 'annualRates', e.target.value)}
                              placeholder="2500"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Annual Insurance</Label>
                            <Input
                              type="number"
                              value={property.annualInsurance}
                              onChange={(e) => updateProperty(property.id, 'annualInsurance', e.target.value)}
                              placeholder="1500"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Annual Maintenance</Label>
                            <Input
                              type="number"
                              value={property.annualMaintenance}
                              onChange={(e) => updateProperty(property.id, 'annualMaintenance', e.target.value)}
                              placeholder="3000"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Annual Depreciation</Label>
                            <Input
                              type="number"
                              value={property.annualDepreciation}
                              onChange={(e) => updateProperty(property.id, 'annualDepreciation', e.target.value)}
                              placeholder="8000"
                            />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              <Button 
                onClick={handleCalculate} 
                className="w-full bg-[#D4AF37] hover:bg-[#C4A037] text-white py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Calculate Tax Benefits
              </Button>

              <div className="bg-[#7A9B76]/10 border border-[#7A9B76]/30 rounded-lg p-4 text-sm">
                <p className="text-[#6B8A67] font-medium">💡 Pro Tip:</p>
                <p className="text-gray-700 mt-1">
                  Negative gearing can reduce your taxable income by thousands. Combined with depreciation, high-income earners can significantly lower their tax bill while building wealth!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {results && (
            <>
              <Card className="border-2 border-[#D4AF37]/30 shadow-xl bg-gradient-to-br from-white to-[#D4AF37]/5">
                <CardHeader className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
                  <CardTitle className="text-2xl text-[#8B7355]">Tax Benefits Summary</CardTitle>
                  <CardDescription>Your investment property portfolio analysis</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white shadow-lg">
                    <p className="text-sm opacity-90 mb-2">Annual Tax Savings</p>
                    <p className="text-4xl font-bold">
                      {formatCurrency(results.summary.taxSavings)}
                    </p>
                    <p className="text-sm opacity-90 mt-2">
                      {formatCurrencyDecimal(results.summary.taxSavings / 52)} per week
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Weekly Cash Flow</p>
                      <p className={`text-xl font-bold ${results.summary.weeklyCashFlow >= 0 ? 'text-green-600' : 'text-[#7A9B76]'}`}>
                        {formatCurrencyDecimal(results.summary.weeklyCashFlow)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Annual Cash Flow</p>
                      <p className={`text-xl font-bold ${results.summary.totalCashFlow >= 0 ? 'text-green-600' : 'text-[#7A9B76]'}`}>
                        {formatCurrency(results.summary.totalCashFlow)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Properties</span>
                      <span className="font-semibold">{results.summary.totalProperties}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Rental Income</span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(results.summary.totalAnnualRentalIncome)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Expenses</span>
                      <span className="font-semibold text-red-600">
                        {formatCurrency(results.summary.totalAnnualExpenses)}
                      </span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="text-sm font-semibold text-gray-700">Net Rental Result</span>
                      <span className={`font-bold ${results.summary.totalNetRentalIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(results.summary.totalNetRentalIncome)}
                      </span>
                    </div>
                  </div>

                  {results.summary.isNegativelyGeared && (
                    <div className="bg-[#7A9B76]/10 border-2 border-[#7A9B76]/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-5 h-5 text-[#7A9B76] mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-[#6B8A67] mb-1">Negatively Geared Portfolio</p>
                          <p className="text-sm text-gray-700">
                            Your properties are negatively geared, meaning you're reducing your taxable income by {formatCurrency(Math.abs(results.summary.totalNetRentalIncome))} annually. This translates to {formatCurrency(results.summary.taxSavings)} in tax savings!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-2 border-[#7A9B76]/20 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Individual Property Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {results.properties.map((property) => (
                    <div 
                      key={property.propertyNumber}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2"
                    >
                      <p className="font-semibold text-gray-800 mb-2">Property {property.propertyNumber}</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Annual Rent:</span>
                          <span className="ml-2 font-semibold text-green-600">
                            {formatCurrency(property.annualRentalIncome)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Total Expenses:</span>
                          <span className="ml-2 font-semibold text-red-600">
                            {formatCurrency(property.totalExpenses)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Cash Flow:</span>
                          <span className={`ml-2 font-semibold ${property.cashFlowBeforeTax >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(property.cashFlowBeforeTax)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Net Income:</span>
                          <span className={`ml-2 font-semibold ${property.netRentalIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(property.netRentalIncome)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
